<template>
  <div></div>
</template>

<script>

import Vue from 'vue';
import Map from 'ol/Map'
import View from 'ol/View'
import Attribution from 'ol/control/Attribution';
import Zoom from 'ol/control/Zoom';
import {
  DragAndDrop,
  defaults as defaultInteractions
} from 'ol/interaction';
import RotateControl from 'ol/control/Rotate';
import Projection from 'ol/proj/Projection';
import TileGrid from 'ol/tilegrid/TileGrid';
import { register as olproj4 } from 'ol/proj/proj4';
import { get as getProj } from 'ol/proj';
import { GPX, GeoJSON, IGC, KML, TopoJSON } from 'ol/format';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import proj4 from 'proj4';
import ImageWMS from 'ol/source/ImageWMS';
import TileWMS from 'ol/source/TileWMS';
// import the app-wide EventBus
import { WguEventBus } from '../../WguEventBus.js';
import { LayerFactory } from '../../factory/Layer.js';
import LayerUtil from '../../util/Layer';
import PermalinkController from './PermalinkController';
import HoverController from './HoverController';
import MapInteractionUtil from '../../util/MapInteraction';
import ViewAnimationUtil from '../../util/ViewAnimation';
import { ColorTheme } from '../../mixins/ColorTheme';
// axios import removed; use centralized store actions for server requests
import { mapState } from 'vuex';

import { GEO_REGION_URL, INS_WMS_URL } from '../../const/const.js';

export default {
  name: 'wgu-map',
  mixins: [ColorTheme],
  props: {
    collapsibleAttribution: { type: Boolean, default: false },
    rotateableMap: { type: Boolean, required: false, default: false },
    target: { type: String, default: 'ol-map-container' }
  },
  data () {
    return {
      zoom: this.$appConfig.mapZoom,
      center: this.$appConfig.mapCenter,
      projection: this.$appConfig.mapProjection,
      projectionObj: null,
      projectionDefs: this.$appConfig.projectionDefs,
      tileGridDefs: this.$appConfig.tileGridDefs || {},
      tileGrids: {},
      permalink: this.$appConfig.permalink,
      mapGeodataDragDop: this.$appConfig.mapGeodataDragDop,
      // mapping format string to OL module / class
      formatMapping: {
        GPX,
        GeoJSON,
        IGC,
        KML,
        TopoJSON
      },
      dragDropLayerCreated: false,
      authArea: null,
      resizeObserver: null,
      // whether the policy tool requests exclusive control over plot visibility
      policyToolActive: false,
      lastRegionFilter: null
    }
  },
  mounted () {
    const me = this;
    // Make the OL map accessible for Mapable mixin even 'ol-map-mounted' has
    // already been fired. Don not use directly in cmps, use Mapable instead.
    Vue.prototype.$map = me.map;

    // Set the target for the OL canvas and tie it`s size to ol-map-container.
    // Remarks: 'ol-map-container' does not exist in the scope of the current unit test,
    // therefore flag the initialization to prevent errors.
    const container = document.getElementById(this.target);
    if (container) {
      me.map.setTarget(container);

      me.resizeObserver = new ResizeObserver(() => {
        if (me.map) {
          me.map.updateSize();
        }
      });
      me.resizeObserver.observe(container);
    }

    // Send the event 'ol-map-mounted' with the OL map as payload
    WguEventBus.$emit('ol-map-mounted', me.map);

    // Listen for policy tool visibility changes so the map can toggle layers
    this._policyToolListener = visible => this.onPolicyToolVisibilityChange(visible);
    WguEventBus.$on('policytool:visibility', this._policyToolListener);

    // TODO
    //  Re-evaluate whether and if yes which of the following operations have to be deferred.
    //  If so, a better implementation could be to rely on this.$nextTick(), which currently causes trouble
    //  for the units tests (deferred operations are invoked after the component has already been destroyed).
    me.timerHandle = setTimeout(() => {
      // adjust the bg color of the OL buttons (like zoom, rotate north, ...)
      me.setOlButtonColor();
    }, 200);
  },
  computed: {
    ...mapState(['authorizedArea', 'selectedArea'])
  },

  destroyed () {
    // Disconnect the ResizeObserver first
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Send the event 'ol-map-unmounted' with the OL map as payload
    WguEventBus.$emit('ol-map-unmounted', this.map);

    // Destroy controllers, remove map references
    if (this.timerHandle) {
      clearTimeout(this.timerHandle);
    }
    if (this.permalinkController) {
      this.permalinkController.tearDown();
      this.permalinkController = undefined;
    }
    if (this.hoverController) {
      this.hoverController.destroy();
      this.hoverController = undefined;
    }
    if (this.map) {
      // Remove zoom level change listener
      this.map.getView().un('change:resolution', this.handleZoomLevelChange);

      this.map.getLayers().clear();
      this.map.getInteractions().clear();
      this.map.getControls().clear();
      this.map.getOverlays().clear();
      this.map.setTarget(null); // Remove map from its target
    }
    // remove policy tool listener if registered
    if (this._policyToolListener) {
      WguEventBus.$off('policytool:visibility', this._policyToolListener);
      this._policyToolListener = null;
    }
    // Clear global reference
    Vue.prototype.$map = null;
    this.map = undefined;
  },
  async created () {
    // make map rotateable according to property
    const interactions = defaultInteractions({
      altShiftDragRotate: this.rotateableMap,
      pinchRotate: this.rotateableMap
    });

    // add geodata drag-drop support according to config
    if (this.mapGeodataDragDop) {
      const dragAndDropInteraction = this.setupGeodataDragDrop();
      interactions.push(dragAndDropInteraction);
    }

    const controls = [
      new Zoom(),
      new Attribution({
        collapsible: this.collapsibleAttribution
      })
    ];
    // add a button control to reset rotation to 0, if map is rotateable
    if (this.rotateableMap) {
      controls.push(new RotateControl());
    }

    // Optional projection (EPSG) definitions for Proj4
    if (this.projectionDefs) {
      // Add all (array of array)
      proj4.defs(this.projectionDefs);
      // Register with OpenLayers
      olproj4(proj4);
    }

    // Projection for map, default is Web Mercator
    let projection;
    if (!this.projection) {
      projection = getProj('EPSG:4326');
    } else {
      projection = new Projection(this.projection);
    }

    // Optional TileGrid definitions by name, for ref in Layers
    Object.keys(this.tileGridDefs).forEach(name => {
      this.tileGrids[name] = new TileGrid(this.tileGridDefs[name]);
    });

    this.map = new Map({
      layers: [],
      controls,
      interactions,
      view: new View({
        center: this.center,
        zoom: this.zoom,
        projection,
        // TODO diff user use different resolutions, so this should be configurable
        maxZoom: 24
      })
    });

    // create layers from config and add them to map
    this.registerLayerEvents();
    const layers = this.createLayers();
    this.map.getLayers().extend(layers);

    // Register a listener to change the source of a layer based on the zoom level
    this.map.getView().on('change:resolution', this.handleZoomLevelChange);

    // Apply initial region filter based on current zoom
    this.applyRegionZoomFilter(this.map.getView().getZoom());

    this.hoverController = this.createHoverController();

    if (this.$appConfig.permalink) {
      this.permalinkController = this.createPermalinkController();
      this.map.set('permalinkcontroller', this.permalinkController, true);
      this.permalinkController.apply();
      this.permalinkController.setup();
    };
    // Fetch the options from the server
    await this.getOptions();
  },

  methods: {
    /**
     * Creates the OL layers due to the "mapLayers" array in app config.
     * @return {ol.layer.Base[]} Array of OL layer instances
     */
    createLayers () {
      const me = this;
      const layers = [];
      const appConfig = this.$appConfig;
      const mapLayersConfig = appConfig.mapLayers || [];
      mapLayersConfig.reverse().forEach(function (lConf) {
        // Some Layers may require a TileGrid object
        // Remarks: Passing null instead of undefined as parameters into the
        //  constructor of OpenLayers sources overwrites OpenLayers defaults.
        lConf.tileGrid = lConf.tileGridRef ? me.tileGrids[lConf.tileGridRef] : undefined;

        // Automatically set the appropriate z-index for the layer type,
        // if not defined explicitly.
        lConf.zIndex = lConf.zIndex ?? (lConf.isBaseLayer ? -1 : 0);

        // Default usage of permalink to true, if not explicitly defined.
        lConf.supportsPermalink = lConf.supportsPermalink ?? true;

        const layer = LayerFactory.getInstance(lConf, me.map);
        layers.push(layer);

        // if layer is selectable register a select interaction
        if (lConf.selectable) {
          const selectClick = MapInteractionUtil.createSelectInteraction(
            layer,
            lConf.selectStyle,
            lConf.doAppendSelectStyle
          )
          // register/activate interaction on map
          me.map.addInteraction(selectClick);
        }
      });

      return layers;
    },

    /**
     * Hook up events to process newly added and modified OL layers.
     * This is currently used to update locale specific layer properties.
     */
    registerLayerEvents () {
      const layers = this.map.getLayers();
      layers.on('add', evt => {
        const layer = evt.element;
        this.updateLocalizedLayerProps(evt.element);
        layer.on('propertychange', evt => {
          if (evt.key === 'lid' || evt.key === 'langKey') {
            this.updateLocalizedLayerProps(evt.target)
          }
        })
      })
    },

    /**
     * Creates a HoverController, override in subclass for specializations.
     *
     * @return {HoverController} HoverController instance.
     */
    createHoverController () {
      return new HoverController(this.map);
    },

    /**
     * Creates a PermalinkController, override in subclass for specializations.
     *
     * @return {PermalinkController} PermalinkController instance.
     */
    createPermalinkController () {
      return new PermalinkController(this.map, this.$appConfig.permalink);
    },

    /**
     * Sets the background color of the OL buttons to the color property.
     */
    setOlButtonColor () {
      const colors = 'secondary onsecondary--text'

      // apply vuetify color by transforming the color to the corresponding
      // CSS class (see https://vuetifyjs.com/en/framework/colors)
      const classes = colors.toString().trim().split(' ');

      // zoom
      if (document.querySelector('.ol-zoom')) {
        classes.forEach(function (c) {
          document.querySelector('.ol-zoom .ol-zoom-in').classList.add(c);
          document.querySelector('.ol-zoom .ol-zoom-out').classList.add(c);
        });
      }

      // rotate
      if (document.querySelector('.ol-rotate')) {
        classes.forEach(function (c) {
          document.querySelector('.ol-rotate .ol-rotate-reset').classList.add(c);
        });
      }
    },

    /**
     * Initializes the geodata drag-drop functionality:
     * Adds the ol/interaction/DragAndDrop to the map and draws the dropped
     * features in a vector layer
     */
    setupGeodataDragDrop () {
      const mapDdConf = this.mapGeodataDragDop;
      const formats = mapDdConf.formats.filter(formatStr => {
        return this.formatMapping[formatStr];
      }).map(fs => {
        return this.formatMapping[fs];
      });

      const dragAndDropInteraction = new DragAndDrop({
        formatConstructors: formats
      });

      dragAndDropInteraction.on('addfeatures', event => {
        let ddSource;

        if (mapDdConf.replaceData !== false) {
          if (!this.dragDropLayerCreated) {
            this.createDragDropLayer(mapDdConf);
            this.dragDropLayerCreated = true;
          }

          // replace existing geodata with the newly dropped data set
          const ddLayer = LayerUtil.getLayersBy(
            'wegueDragDropLayer', true, this.map)[0];
          ddSource = ddLayer.getSource();
          ddSource.clear();
        } else {
          // add new layer for each dropped data set
          const newDdLayer = this.createDragDropLayer(mapDdConf);
          ddSource = newDdLayer.getSource();
        }

        ddSource.addFeatures(event.features);

        if (mapDdConf.zoomToData === true) {
          ViewAnimationUtil.to(this.map.getView(), ddSource.getExtent());
        }
      }, this);

      return dragAndDropInteraction;
    },

    /**
     * Creates the vector layer for showing drag/drop geodata on the map.
     *
     * @param {Object} mapDdConf the config object for this functionality
     */
    createDragDropLayer (mapDdConf) {
      const vectorSource = new VectorSource({});
      const vectorLayer = new VectorLayer({
        // random unique layer ID
        // For localization the randomized layer ID cannot be used, therefore map it to a fixed key
        // which will be part of the language path ('mapLayers.wgu-drag-drop-layer')
        lid: 'wgu-drag-drop-layer-' + (Math.random() * 1000000).toFixed(0),
        langKey: 'wgu-drag-drop-layer',
        wegueDragDropLayer: true,
        source: vectorSource,
        displayInLayerList: mapDdConf.displayInLayerList
      });
      this.map.addLayer(vectorLayer);

      return vectorLayer;
    },

    /**
     * Updates locale specific layer properties. This is typically invoked after changes to
     * layer attributes, changes to the OpenLayers layer collection to process new layers
     * or after changes to the active locale.
     *
     * Remarks:
     * If a layer definition exists in the application context, any 'name' or 'attribution'
     * property declared there will take precedence over the ones declared in the
     * language packs.
     *
     * @param {ol.layer.Layer} OL layer instance
     */
    updateLocalizedLayerProps (layer) {
      const langKey = layer.get('langKey') || layer.get('lid');
      const pathLayer = 'mapLayers.' + langKey;

      // Update layer name.
      const pathName = pathLayer + '.name';
      layer.set('name', layer.get('confName') || this.$t(pathName));

      // Update optional layer attributions.
      const pathAttributions = pathLayer + '.attributions';
      const source = layer.getSource();
      if (source &&
        (typeof source.setAttributions === 'function') &&
        (layer.get('confAttributions') || this.$te(pathAttributions))) {
        source.setAttributions(layer.get('confAttributions') || this.$t(pathAttributions));
      }
    },

    /**
     * Handles the change in zoom level of the map.
     * Updates the CQL_FILTER_SQL based on the new zoom level and sets the source of the 'region' layer.
     *
     * @param {Event} evt - The event object representing the zoom level change.
     */
    handleZoomLevelChange (evt) {
      const zoom = evt.target.getZoom();
      this.applyRegionZoomFilter(zoom);

      // If policy tool controls plot visibility, skip map-level plot toggling
      if (!this.policyToolActive) {
        if (zoom > 13) {
          this.map.getLayers().forEach(layer => {
            if (layer.get('lid') === 'plot') {
              layer.setVisible(true);

              // update the CQL_FILTER_SQL based on the user authentication level
              const cqlStatements = this.generateCqlInStatement(this.authArea);
              const newSource = new TileWMS({
                url: INS_WMS_URL,
                params: {
                  LAYERS: 'ins:ins_plot_insured',
                  CQL_FILTER: cqlStatements.villageCql || ''
                },
                serverType: 'geoserver',
                transition: 0,
                tileLoadFunction: function (image, src) {
                  // 创建一个HTTP请求来获取图像
                  const xhr = new XMLHttpRequest();
                  xhr.open('GET', src);
                  xhr.responseType = 'blob'; // 期望的响应类型是blob
                  // 设置Authorization请求头
                  xhr.setRequestHeader('Authorization', 'Basic ' + window.btoa('data_services:data_services'));
                  // 请求加载成功时的处理
                  xhr.onload = function () {
                    if (this.status === 200) {
                      const blob = this.response;
                      // 创建一个对象URL
                      const objectUrl = URL.createObjectURL(blob);
                      image.getImage().src = objectUrl;
                    }
                  };
                  // 发送请求
                  xhr.send();
                }
              });
              layer.setSource(newSource);
            }
          });
        } else {
          this.map.getLayers().forEach(layer => {
            if (layer.get('lid') === 'plot') {
              layer.setVisible(false);
            }
          });
        }
      }
    },

    /**
     * Applies region layer filter based on zoom, only when the filter changes.
     * @param {number} zoom
     */
    applyRegionZoomFilter (zoom) {
      if (!this.map) return;

      const cqlFilter = this.determineZoomLevelFilter(zoom);
      if (cqlFilter === this.lastRegionFilter) return;

      const layer = this.map.getLayers().getArray().find(l => l.get('lid') === 'region-layer');
      if (!layer) return;

      const source = layer.getSource && layer.getSource();
      if (source && typeof source.updateParams === 'function') {
        source.updateParams({ LAYERS: 'ks:geo_district_code', CQL_FILTER: cqlFilter });
      } else {
        const newSource = new ImageWMS({
          url: GEO_REGION_URL,
          params: { LAYERS: 'ks:geo_district_code', CQL_FILTER: cqlFilter },
          FORMAT: 'image/jpeg',
          TRANSPARENT: true,
          serverType: 'geoserver',
          transition: 0
        });
        layer.setSource(newSource);
      }

      layer.setZIndex(90);
      this.lastRegionFilter = cqlFilter;
    },

    /**
     * Determines the filter expression based on the zoom level.
     * The filter expression is used to filter the 'region' layer based on the zoom level and user authentication level.
     *
     * @param {number} zoom - The zoom value.
     * @returns {string} The filter expression based on the zoom level.
     */
    determineZoomLevelFilter (zoom) {
      if (zoom < 7) return 'dist_level_code = 1';
      if (zoom < 9.5) return 'dist_level_code in (1,2)';
      if (zoom < 11) return 'dist_level_code in (2,3)';
      if (zoom < 13) return 'dist_level_code in (3,4)';
      return 'dist_level_code in (4,5)';
    },

    /**
     * Fetch the auth regions from the server.
     */
    async getOptions () {
      // Use centralized Vuex action to fetch and cache auth regions
      const data = await this.$store.dispatch('fetchAuthRegions');
      if (!data) return;
      console.log('auth-region data from store action:', data);
      this.optionData = data;
      // store action already committed authorizedArea and authRegions; for local compatibility set authArea
      this.authArea = data.auth_region || [];
      // Prefer explicit selectedArea.place_id when present (user selection);
      // otherwise fall back to authArea returned from server.
      let villageCql = '';
      const sel = this.$store && this.$store.state && this.$store.state.selectedArea ? this.$store.state.selectedArea : this.selectedArea;
      if (sel && sel.place_id) {
        // place_id is used as village_code in CQL
        villageCql = `village_code IN ('${sel.place_id}')`;
      } else {
        const cqlStatements = this.generateCqlInStatement(this.authArea);
        villageCql = cqlStatements.villageCql || '';
      }

      console.log('Applying CQL to underwritten_plots:', villageCql);
      // Apply CQL to underwritten_plots layer if present
      this.setUnderwrittenPlotsCql(villageCql);
    },
    // 生成 CQL IN 语句
    generateCqlInStatement (data) {
      if (!data || !data.village) {
        console.log('data is null, undefined, or missing village property');
        return {
          villageCql: ''
        };
      }

      const villageCodes = data.village.map(code => `'${code}'`);
      return {
        villageCql: `village_code IN (${villageCodes.join(', ')})`
      };
    },

    /**
     * Apply a CQL_FILTER to the underwritten_plots WMS layer.
     * Accepts an empty string to clear the filter.
     * @param {string} cql
     */
    setUnderwrittenPlotsCql (cql) {
      if (!this.map) return;
      // Delegate to LayerFactory so logic lives in layer.js
      try {
        LayerFactory.setCqlFilterByLid(this.map, 'underwritten_plots', cql);
      } catch (e) {
        // no-op
      }
    },
    /**
     * Toggle visibility so Policy tool can exclusively show underwritten_plots
     * When visible === true: hide 'plot' layers and show only 'underwritten_plots'
     * When visible === false: restore previous visibility states
     */
    onPolicyToolVisibilityChange (visible) {
      // set active flag so other logic (zoom handler) can skip plot toggling
      this.policyToolActive = !!visible;

      // cache previous visibility if not yet stored
      if (!this._policyVisibilityCache) this._policyVisibilityCache = {};

      const layers = this.map.getLayers().getArray();
      layers.forEach(layer => {
        const lid = layer.get('lid');
        if (!lid) return;

        if (visible) {
          // store previous state
          if (!(lid in this._policyVisibilityCache)) {
            this._policyVisibilityCache[lid] = layer.getVisible();
          }

          if (lid === 'underwritten_plots') {
            layer.setVisible(true);
          } else if (lid === 'plot') {
            layer.setVisible(false);
          }
        } else {
          // restore previous visibility if known, else leave unchanged
          if (lid in this._policyVisibilityCache) {
            layer.setVisible(!!this._policyVisibilityCache[lid]);
          }
        }
      });

      // clear cache when policy tool hidden
      if (!visible) this._policyVisibilityCache = null;
    }
  },
  watch: {
    /**
     * Watch for locale changes and update language specific layer attributes.
     */
    '$i18n.locale': function () {
      const layers = this.map.getLayers();
      layers.forEach(layer => {
        this.updateLocalizedLayerProps(layer);
      });
    },
    /**
     * Watch for changes in the authorized area and update the map view accordingly.
     */
    authorizedArea: {
      handler (newVal) {
        if (newVal && newVal.length > 0) {
          // Update the map view based on the new authorized area
          this.map.getView().setMaxZoom(newVal[0]?.organize === 'ahic' ? 18 : 20);
        }
      }
    },

    // When the user changes the selectedArea (e.g. via UI), reapply the CQL to underwritten_plots
    selectedArea: {
      handler (newVal) {
        if (!newVal) {
          this.setUnderwrittenPlotsCql('');
          return;
        }
        if (newVal.place_id) {
          const cql = `village_code IN ('${newVal.place_id}')`;
          this.setUnderwrittenPlotsCql(cql);
        }
      },
      deep: true
    }
  }

}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style>
div.ol-zoom {
  top: auto;
  left: auto;
  bottom: 3em;
  right: 0.5em;
}

div.ol-attribution.ol-uncollapsible {
  font-size: 10px;
}
</style>
